import { Archive, CheckCircle2, RotateCcw, Send } from "lucide-react";
import * as React from "react";

import { type FormDef, type FormVersionMeta, formsApi } from "@/api/forms";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useUIStore } from "@/stores/ui";

interface Props {
  form: FormDef;
  onRefresh: () => void | Promise<void>;
  onLoadVersion: (version: number) => void | Promise<void>;
}

export function VersionTimeline({ form, onRefresh, onLoadVersion }: Props): JSX.Element {
  const pushToast = useUIStore((s) => s.pushToast);
  const [busy, setBusy] = React.useState<number | null>(null);

  const publish = async (v: number): Promise<void> => {
    setBusy(v);
    try {
      await formsApi.publish(form.code, v);
      pushToast({ variant: "success", title: `Đã publish v${v}` });
      await onRefresh();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Publish thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(null);
    }
  };

  const rollback = async (v: number): Promise<void> => {
    if (!confirm(`Rollback về v${v}? Sẽ tạo version mới (copy từ v${v}) và publish.`)) {
      return;
    }
    setBusy(v);
    try {
      const newV = await formsApi.rollback(form.code, v);
      pushToast({
        variant: "success",
        title: `Rollback xong → v${newV.version}`,
      });
      await onRefresh();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Rollback thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(null);
    }
  };

  if (!form.versions.length) {
    return (
      <p className="text-sm text-muted-foreground p-3">Chưa có version nào.</p>
    );
  }

  return (
    <div className="divide-y border rounded-md bg-background">
      {form.versions.map((v) => (
        <VersionRow
          key={v.id}
          v={v}
          isPublished={v.version === form.published_version}
          busy={busy === v.version}
          onPublish={() => void publish(v.version)}
          onRollback={() => void rollback(v.version)}
          onLoad={() => void onLoadVersion(v.version)}
        />
      ))}
    </div>
  );
}

function VersionRow({
  v,
  isPublished,
  busy,
  onPublish,
  onRollback,
  onLoad,
}: {
  v: FormVersionMeta;
  isPublished: boolean;
  busy: boolean;
  onPublish: () => void;
  onRollback: () => void;
  onLoad: () => void;
}): JSX.Element {
  const date = v.published_at ?? v.created_at;
  return (
    <div className="p-3 flex items-center gap-3 text-sm">
      <div className="font-mono font-semibold">v{v.version}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <StatusBadge status={v.status} isPublished={isPublished} />
          {v.change_notes ? (
            <span className="text-xs text-muted-foreground truncate">
              "{v.change_notes}"
            </span>
          ) : null}
        </div>
        <div className="text-[11px] text-muted-foreground mt-0.5">
          {new Date(date).toLocaleString("vi-VN")}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button size="sm" variant="ghost" onClick={onLoad} disabled={busy}>
          Mở
        </Button>
        {v.status === "draft" ? (
          <Button size="sm" onClick={onPublish} disabled={busy}>
            <Send className="size-3.5 mr-1" /> Publish
          </Button>
        ) : v.status === "archived" || (v.status === "published" && !isPublished) ? (
          <Button size="sm" variant="outline" onClick={onRollback} disabled={busy}>
            <RotateCcw className="size-3.5 mr-1" /> Rollback
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function StatusBadge({
  status,
  isPublished,
}: {
  status: string;
  isPublished: boolean;
}): JSX.Element {
  if (isPublished) {
    return (
      <Badge variant="default" className="text-[10px]">
        <CheckCircle2 className="size-3 mr-1" /> Published (live)
      </Badge>
    );
  }
  if (status === "draft") {
    return (
      <Badge variant="outline" className="text-[10px]">
        Draft
      </Badge>
    );
  }
  if (status === "archived") {
    return (
      <Badge variant="outline" className="text-[10px] text-muted-foreground">
        <Archive className="size-3 mr-1" /> Archived
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="text-[10px]">
      {status}
    </Badge>
  );
}
